import { NgForm } from '@angular/forms';
import { ParamMap } from '@angular/router';
import * as dayjs from 'dayjs';
import { CallbackDataParams, TooltipFormatterCallback, TopLevelFormatterParams } from 'echarts/types/dist/shared';
import * as _ from 'lodash';
import { UiConfig } from '../resources/ui-configs.resource';
import { DeepPartial } from './types';

export function markAsPristine(form?: NgForm | null | undefined): void {
  if (form != null) {
    form.form.markAsPristine();
    form.form.markAsUntouched();
  } else {
    console.error('Form is not initialized, can\'t mark as pristine');
  }
}

export function markAsDirty(form?: NgForm | null | undefined): void {
  if (form != null) {
    form.form.markAsDirty();
  } else {
    console.error('Form is not initialized, failed to set as dirty!');
  }
}

export enum PeriodUnit {
  DAY, WEEK, MONTH,
}

export function mergeConfigurations(defaults: UiConfig, ...configurations: DeepPartial<UiConfig>[]): UiConfig;
export function mergeConfigurations(defaults: null, ...configurations: DeepPartial<UiConfig>[]): DeepPartial<UiConfig>;
export function mergeConfigurations(defaults: UiConfig | null, ...configurations: DeepPartial<UiConfig>[]) {
  const mergedConfig: UiConfig | DeepPartial<UiConfig> = defaults != null ? _.cloneDeep(defaults) : {};
  if (configurations == null || configurations.length === 0) {
    return mergedConfig;
  }
  for (const configuration of configurations) {
    if (configuration && configuration.personalization) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      mergedConfig.personalization = mergeConfigurationParts(
        mergedConfig.personalization ?? {},
        configuration.personalization,
      );
    }
  }
  for (let i = configurations.length - 1; i > -1; i--) {
    if (configurations[i] && configurations[i].configuration) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      mergedConfig.configuration = mergeConfigurationParts(
        mergedConfig.configuration ?? {},
        configurations[i].configuration,
      );
    }
  }
  return mergedConfig;
}

export function mergeConfigurationParts(...parts: any): any {
  const result = parts[0];

  function customizer(objValue: any, srcValue: any): any {
    if (_.isArray(objValue)) {
      return _.clone(srcValue) || _.clone(objValue);
    }
    return undefined;
  }

  for (let i = 1; i < parts.length; i++) {
    _.mergeWith(result, parts[i], customizer);
  }
  return result;
}

export function shallowInitBooleanValues(obj: any, defaults: any): void {
  for (const key of Object.keys(defaults)) {
    if (typeof defaults[key] === 'boolean') {
      if (obj == null || obj[key] == null) {
        if (obj == null) {
          obj = {};
        }
        obj[key] = defaults[key];
      }
    }
  }
}

export function noDefaults(obj: any, defaults: any): any {
  if ((obj instanceof Array) && (defaults instanceof Array)) {
    if (obj.length !== defaults.length) {
      return obj;
    }
    for (let i = 0; i < obj.length; i++) {
      if (obj[i] !== defaults[i]) {
        return obj;
      }
    }
    return undefined;
  }
  if ((obj instanceof Array) || (defaults instanceof Array)) {
    return _.clone(obj);
  }
  if (typeof obj === 'object' && typeof defaults === 'object') {
    const result: any = {};
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const prop in obj) {
      const res = prop in defaults ? noDefaults(obj[prop], defaults[prop]) : _.clone(obj[prop]);
      if (res != null) {
        result[prop] = res;
      }
    }
    return _.isEmpty(result) ? undefined : result;
  }
  return _.isEqual(obj, defaults) ? undefined : _.clone(obj);
}

export const queryParamValueSeparator = ',';

export function parseNumber(serializedNumber: string | null | undefined): number | undefined {
  if (serializedNumber == null) {
    return undefined;
  }
  const value = Number(serializedNumber);
  if (!Number.isNaN(value) && Number.isFinite(value)) {
    return value;
  }
  return undefined;
}

export function readDateFromParamMap(paramMap: ParamMap, key: string): Date | undefined {
  const serializedValue = parseNumber(paramMap.get(key));
  if (serializedValue == null) {
    return undefined;
  }
  return new Date(serializedValue);
}

export function readNumberFromParamMap(paramMap: ParamMap, key: string): number | undefined {
  return parseNumber(paramMap.get(key));
}

export function readBooleanFromParamMap(paramMap: ParamMap, key: string): boolean | undefined {
  const serializedValue = paramMap.get(key);
  if (serializedValue == null) {
    return undefined;
  }
  return serializedValue === 'true';
}

export function readNumberArrayFromParamMap(paramMap: ParamMap, key: string): number[] | undefined {
  const serializedValue = paramMap.get(key);
  if (serializedValue == null) {
    return undefined;
  }
  const result: number[] = [];
  for (const part of serializedValue.split(queryParamValueSeparator)) {
    const parsedNumber = parseNumber(part);
    if (parsedNumber != null) {
      result.push(parsedNumber);
    }
  }
  return result;
}

export function readStringArrayFromParamMap(paramMap: ParamMap, key: string): string[] | undefined {
  const serializedValue = paramMap.get(key);
  if (serializedValue == null) {
    return undefined;
  }
  return serializedValue.split(queryParamValueSeparator);
}

export function parseEnumValue<T>(serializedValue: string | null | undefined, enumObj: T): T[keyof T] | undefined {
  for (const entry of Object.entries(enumObj)) {
    if (entry[1] === serializedValue) {
      return entry[1];
    }
  }
  return undefined;
}

export function readEnumValueFromParamMap<T>(paramMap: ParamMap, key: string, enumObj: T): T[keyof T] | undefined {
  const serializedValue = paramMap.get(key);
  if (serializedValue == null) {
    return undefined;
  }
  for (const entry of Object.entries(enumObj)) {
    if (entry[1] === serializedValue) {
      return entry[1];
    }
  }
  return undefined;
}

export function readEnumArrayFromParamMap<T>(paramMap: ParamMap, key: string, enumObj: T): (T[keyof T])[] | undefined {
  const serializedValue = paramMap.get(key);
  if (serializedValue == null) {
    return undefined;
  }
  const result: (T[keyof T])[] = [];
  for (const part of serializedValue.split(queryParamValueSeparator)) {
    const parsedValue = parseEnumValue(part, enumObj);
    if (parsedValue != null) {
      result.push(parsedValue);
    }
  }
  return result;
}

export function serializeArray(array: any[] | undefined): string | undefined {
  if (array == null) {
    return undefined;
  }
  if (array.length === 0) {
    return undefined;
  }
  let result = '';
  for (const item of array) {
    result = `${result}${queryParamValueSeparator}${String(item)}`;
  }
  return result.substring(1);
}

export function printISODate(d?: Date | number): string {
  if (!d) {
    return '';
  }
  const date = (typeof d === 'number') ? dayjs(new Date(d)) : dayjs(d);
  return date.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
}

export function printTime(
  daysLabel: string,
  hoursLabel: string,
  minutesLabel: string,
  secondsLabel: string,
  neverLabel: string,
  value: number | string | null | undefined,
): string {
  if (value == null) {
    return neverLabel;
  }

  const parsed: number = Number(value);
  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
    // Not a number
    return neverLabel;
  }

  let residue;
  const days = parsed > 0 ? Math.floor(parsed / 86400) : Math.ceil(parsed / 86400);
  residue = parsed % 86400;
  const hours = residue > 0 ? Math.floor(residue / 3600) : Math.ceil(residue / 3600);
  residue %= 3600;
  const minutes = residue > 0 ? Math.floor(residue / 60) : Math.ceil(residue / 60);
  const seconds = residue > 0 ? Math.floor(residue % 60) : Math.ceil(residue % 60);

  // console.log(`parsed:${parsed}, days:${days} `);

  if (days) {
    return `${days}${daysLabel} ${hours}${hoursLabel}`;
  } if (hours) {
    return `${hours}${hoursLabel} ${minutes}${minutesLabel}`;
  } if (minutes) {
    return `${minutes}${minutesLabel} ${seconds}${secondsLabel}`;
  }

  return `${seconds}${secondsLabel}`;
}

function getEchartsTooltip(params: CallbackDataParams, value: string) {
  return `${params.marker} ${params.name} <b>${value}</b>`;
}

export function getEchartsTooltipFormatter(
  formatter: (value: any) => string,
): TooltipFormatterCallback<TopLevelFormatterParams> {
  return (params: TopLevelFormatterParams): string | HTMLElement[] => {
    if (params instanceof Array) {
      let result = '';
      for (const param of params) {
        result = `${result}<p>${getEchartsTooltip(param, formatter(param.value))}</p>`;
      }
      return result;
    }
    return getEchartsTooltip(params, formatter(params.value));
  };
}

export function getDateTime(hours: number, minutes: number, addDays?: number, addMonths?: number): Date {
  return dayjs().startOf('day')
    .add(addDays === undefined ? 0 : addDays, 'day')
    .add(addMonths === undefined ? 0 : addMonths, 'month')
    .hour(hours)
    .minute(minutes)
    .toDate();
}

export function getStartTime(unit: PeriodUnit, last? : boolean): Date {
  const offset = last ? -1 : 0;
  if (unit === PeriodUnit.DAY) {
    return dayjs().startOf('day').add(offset, 'day')
      .toDate();
  } if (unit === PeriodUnit.WEEK) {
    return dayjs().startOf('week').add(1, 'day').add(offset, 'week')
      .toDate();
  } if (unit === PeriodUnit.MONTH) {
    return dayjs().startOf('month').add(offset, 'month')
      .toDate();
  }
  console.warn('unsupported unit');
  return dayjs().toDate();
}

export function getEndTime(unit: PeriodUnit, last? : boolean): Date {
  const offset = last ? -1 : 0;
  if (unit === PeriodUnit.DAY) {
    return dayjs().endOf('day').add(1).add(offset, 'day')
      .toDate();
  } if (unit === PeriodUnit.WEEK) {
    return dayjs().endOf('week').add(1, 'day').add(1)
      .add(offset, 'week')
      .toDate();
  } if (unit === PeriodUnit.MONTH) {
    return dayjs().endOf('month').add(1).add(offset, 'month')
      .toDate();
  }
  console.warn('unsupported unit');
  return dayjs().toDate();
}

export function isNonNull<T>(value: T): value is NonNullable<T> {
  return value != null;
}

export function isNumber(value: string | number): value is number {
  return !Number.isNaN(Number(value));
}

export function secsToHours(sec: number): number {
  return Math.round((sec / 36)) / 100;
}

export function getKeeperSubheader(record: any): string {
  let result = '';
  if (record.position) {
    result += `${record.position}, `;
  }
  if (record.department) {
    result += `${record.department}, `;
  }
  if (record.organization) {
    result += `${record.organization}, `;
  }
  if (result.length > 0) {
    result = result.substring(0, result.length - 2);
  }
  return result;
}
